import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AnimatedSection } from "@/components/site/Motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import {
  ArrowRight,
  BrainCircuit,
  Check,
  Cloud,
  Code2,
  Infinity,
  LineChart,
  Sparkles,
  ShieldCheck,
  Timer,
  Users,
} from "lucide-react";
import { caseStudies } from "@/mock/mock";
import { cn } from "@/lib/utils";

const heroSlides = [
  {
    title: "Engineering Scalable Software.",
    accent: "Without the Chaos.",
    body: "Enterprise-grade systems built with AI, cloud, and modern engineering practices, delivered with clarity and control.",
    primary: "Start a Conversation",
    secondary: "View Our Work",
    to: "/contact",
    secondaryTo: "/projects",
    variant: "brand",
    visual: "/assets/images/hero/slide1.png",
    proof: [
      { icon: Cloud, label: "AI + Cloud Native", detail: "Development" },
      { icon: Code2, label: "MVP to", detail: "Enterprise Scale" },
      { icon: ShieldCheck, label: "Fixed Scope.", detail: "Transparent Pricing." },
    ],
  },
  {
    title: "From MVP to",
    accent: "Enterprise Systems.",
    afterAccent: "Built Right.",
    body: "Full-stack engineering across AI applications, cloud platforms, and scalable architectures, designed to grow with your business.",
    primary: "Explore Products",
    secondary: "See Case Studies",
    to: "/products",
    secondaryTo: "/projects",
    variant: "dashboard",
    proof: [
      { icon: BrainCircuit, label: "AI Applications" },
      { icon: Cloud, label: "Cloud Engineering" },
      { icon: Code2, label: "Full-Stack Systems" },
      { icon: Infinity, label: "DevOps & CI/CD" },
    ],
  },
  {
    title: "Have a Product in Mind?",
    accent: "Let's Build It Right.",
    body: "Work with a team that values structured communication, ownership, and predictable delivery.",
    primary: "Book a Strategy Call",
    to: "/contact",
    variant: "meeting",
    visual: "/assets/images/hero/slide3bg.png",
    proof: [{ icon: Users, label: "We work best with teams serious about execution." }],
  },
];

const HeroButton = ({ children, to, variant = "primary" }) => (
  <Button
    asChild
    size="lg"
    variant={variant === "primary" ? "default" : "outline"}
    className={cn(
      "h-12 w-full max-w-full justify-center rounded-md px-5 text-sm sm:w-auto sm:px-6 sm:text-base",
      variant === "primary"
        ? "bg-primary text-primary-foreground hover:bg-primary/90"
        : "border-primary/70 bg-white/70 text-foreground hover:bg-primary/5"
    )}
  >
    <Link to={to} className="inline-flex items-center gap-3">
      {children}
      <ArrowRight className="h-4 w-4" />
    </Link>
  </Button>
);

const HeroProof = ({ items }) => (
  <div className="mt-8 grid gap-4 text-sm text-foreground/90 sm:flex sm:flex-wrap sm:gap-x-5 sm:gap-y-4">
    {items.map(({ icon: Icon, label, detail }) => (
      <div key={`${label}-${detail || ""}`} className="flex min-w-0 items-center gap-3">
        <Icon className="h-6 w-6 shrink-0 text-primary sm:h-7 sm:w-7" />
        <span className="leading-tight">
          <span>{label}</span>
          {detail ? <span className="block">{detail}</span> : null}
        </span>
      </div>
    ))}
  </div>
);

const WaveField = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden">
    <div className="hero-wave hero-wave-one" />
    <div className="hero-wave hero-wave-two" />
    <div className="hero-dot-field" />
  </div>
);

const BrandVisual = ({ src }) => (
  <div className="pointer-events-none relative hidden h-[560px] overflow-hidden lg:flex lg:items-center lg:justify-center">
    <img
      src={src}
      alt=""
      aria-hidden="true"
      className="h-full w-full object-contain object-center"
      loading="eager"
    />
    <div className="absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-white via-white/80 to-transparent" />
  </div>
);

const MeetingVisual = ({ src }) => (
  <div className="pointer-events-none relative hidden h-[560px] overflow-hidden lg:block">
    <img
      src={src}
      alt=""
      aria-hidden="true"
      className="h-full w-full object-cover object-center"
      loading="lazy"
    />
    <div className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-white via-white/95 to-transparent" />
    <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-white to-transparent" />
  </div>
);

const DashboardVisual = () => {
  const metrics = [
    ["Active Users", "24.8K", "+12.5%"],
    ["Performance", "98.6%", "+8.2%"],
    ["Deployments", "126", "+15.3%"],
    ["Response Time", "120ms", "-14.8%"],
  ];
  const deployments = ["AI Recommendation Engine", "Payment Service", "User Management", "Analytics Dashboard"];

  return (
    <div className="relative hidden h-[560px] overflow-hidden lg:flex lg:items-center">
      <WaveField />
      <div className="relative mx-auto w-full max-w-[520px] origin-center scale-[0.88] rounded-2xl border border-black/10 bg-white/82 p-3 shadow-[0_30px_80px_-55px_rgba(0,0,0,0.5)] backdrop-blur-sm xl:scale-95">
        <div className="grid gap-3 md:grid-cols-[0.75fr_2.4fr]">
          <div className="hidden rounded-xl border border-black/10 bg-white/65 p-2 text-[11px] text-black/70 md:block">
            {["Overview", "Applications", "Analytics", "Infrastructure", "Deployments", "Monitoring", "Settings"].map((item, index) => (
              <div
                key={item}
                className={cn(
                  "mb-1.5 rounded-md px-2 py-1.5",
                  index === 0 && "border border-primary/70 bg-primary/5 text-foreground"
                )}
              >
                {item}
              </div>
            ))}
          </div>
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              {metrics.map(([label, value, change]) => (
                <div key={label} className="rounded-xl border border-black/10 bg-white p-2.5">
                  <div className="text-[11px] text-black/70">{label}</div>
                  <div className="mt-2 flex items-end justify-between gap-2">
                    <div className="text-base font-semibold">{value}</div>
                    <div className="text-[11px] font-medium text-emerald-600">{change}</div>
                  </div>
                  <LineChart className="mt-1.5 h-6 w-full text-primary" />
                </div>
              ))}
            </div>
            <div className="grid gap-3">
              <div className="rounded-xl border border-black/10 bg-white p-3">
                <div className="text-sm font-medium">System Architecture</div>
                <div className="mt-4 grid gap-2 text-[11px]">
                  <div className="flex items-center gap-2">
                    <span className="rounded-md border border-primary/70 px-2 py-1.5">Web App</span>
                    <span className="h-px flex-1 bg-primary/40" />
                    <span className="rounded-md border border-primary/70 px-2 py-1.5">API Gateway</span>
                  </div>
                  <div className="ml-auto grid w-3/4 grid-cols-2 gap-2">
                    {["Auth Service", "Data Service", "AI Service", "Analytics"].map((item) => (
                      <span key={item} className="rounded-md border border-primary/70 px-2 py-1.5 text-center">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="rounded-xl border border-black/10 bg-white p-3">
                <div className="text-sm font-medium">Recent Deployments</div>
                <div className="mt-3 grid gap-2 text-[11px]">
                  {deployments.map((item) => (
                    <div key={item} className="flex items-center gap-2">
                      <span className="grid h-5 w-5 place-items-center rounded-full bg-primary text-white">
                        <Check className="h-3 w-3" />
                      </span>
                      <span className="min-w-0 flex-1 truncate">{item}</span>
                      <Check className="h-3 w-3 text-emerald-600" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const HeroVisual = ({ variant, visual }) => {
  if (variant === "dashboard") return <DashboardVisual />;
  if (variant === "meeting") return <MeetingVisual src={visual} />;
  return <BrandVisual src={visual} />;
};

const SectionTitle = ({ eyebrow, title, desc }) => (
  <div className="max-w-2xl">
    {eyebrow ? (
      <div className="text-xs font-semibold tracking-widest text-primary/90">
        {eyebrow}
      </div>
    ) : null}
    <h2 className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight">
      {title}
    </h2>
    {desc ? <p className="mt-3 text-sm sm:text-base text-muted-foreground">{desc}</p> : null}
  </div>
);

export default function Home() {
  const [email, setEmail] = useState("");
  const [activeHero, setActiveHero] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveHero((current) => (current + 1) % heroSlides.length);
    }, 6500);

    return () => window.clearInterval(timer);
  }, []);

  const onNewsletter = (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      toast({ title: "Add your email", description: "We’ll send only occasional updates." });
      return;
    }
    const existing = JSON.parse(localStorage.getItem("ellipsonic_newsletter") || "[]");
    const next = Array.from(new Set([trimmed, ...existing]));
    localStorage.setItem("ellipsonic_newsletter", JSON.stringify(next));
    setEmail("");
    toast({ title: "Subscribed", description: "You’re on the list. Thank you." });
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <div className="relative mx-auto w-full max-w-6xl px-4">
          <div className="relative min-h-[660px] overflow-hidden bg-white sm:min-h-[700px] lg:min-h-[680px] lg:max-h-[760px]">
            {heroSlides.map((slide, index) => (
              <div
                key={slide.title}
                className={cn(
                  "absolute inset-0",
                  index === activeHero ? "z-10 block" : "hidden"
                )}
                aria-hidden={index !== activeHero}
              >
                <div
                  className={cn(
                    "relative grid min-h-[660px] max-w-full gap-8 overflow-hidden py-12 sm:min-h-[700px] lg:min-h-[680px] lg:grid-cols-[minmax(0,0.54fr)_minmax(0,0.46fr)] lg:items-center lg:py-14"
                  )}
                >
                  {slide.variant === "dashboard" ? <WaveField /> : null}
                  <div
                    className={cn(
                      "relative z-10 w-full max-w-[350px] self-center sm:max-w-3xl lg:max-w-none"
                    )}
                  >
                    <h1
                      className={cn(
                        "max-w-full text-4xl font-semibold leading-[1.06] tracking-tight text-black sm:text-5xl lg:text-[2.95rem] xl:text-[3.15rem]"
                      )}
                    >
                      {slide.title}
                      <span className="block text-primary">{slide.accent}</span>
                      {slide.afterAccent ? <span className="block">{slide.afterAccent}</span> : null}
                    </h1>
                    <p className="mt-5 max-w-full text-base leading-7 text-muted-foreground sm:max-w-2xl sm:text-lg lg:max-w-[690px]">
                      {slide.body}
                    </p>

                    <div className="mt-7 flex w-full max-w-full flex-col gap-3 sm:w-auto sm:flex-row">
                      <HeroButton to={slide.to}>{slide.primary}</HeroButton>
                      {slide.secondary ? (
                        <HeroButton to={slide.secondaryTo} variant="secondary">
                          {slide.secondary}
                        </HeroButton>
                      ) : null}
                    </div>

                    <HeroProof items={slide.proof} />
                  </div>

                  <HeroVisual variant={slide.variant} visual={slide.visual} />
                </div>
              </div>
            ))}
          </div>

          <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 items-center gap-3">
            {heroSlides.map((slide, index) => (
              <button
                key={slide.title}
                type="button"
                onClick={() => setActiveHero(index)}
                className={cn(
                  "h-3 w-3 rounded-full bg-black/15 transition-all duration-300 hover:bg-primary/70",
                  index === activeHero && "w-8 bg-primary"
                )}
                aria-label={`Show hero slide ${index + 1}`}
                aria-current={index === activeHero ? "true" : undefined}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Why Ellipsonic */}
      <AnimatedSection as="section" delay={0} className="mx-auto max-w-6xl px-4 py-12">
        <SectionTitle
          eyebrow="Why Ellipsonic"
          title="A delivery model designed for trust"
          desc="Strong engineering fundamentals with an enterprise mindset — without losing agility."
        />

        <div className="mt-8 grid gap-4 lg:grid-cols-4">
          <Card className="border-border/70 bg-background/60 backdrop-blur-[16px]">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" /> Engineering‑first
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              Architecture, testing discipline, and measurable baselines from day one.
            </CardContent>
          </Card>
          <Card className="border-border/70 bg-background/60 backdrop-blur-[16px]">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" /> Business‑aligned
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              We translate outcomes into systems — not slide decks.
            </CardContent>
          </Card>
          <Card className="border-border/70 bg-background/60 backdrop-blur-[16px]">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Timer className="h-4 w-4 text-primary" /> Agile + Enterprise
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              Fast iterations with governance, security, and predictable delivery.
            </CardContent>
          </Card>
          <Card className="border-border/70 bg-background/60 backdrop-blur-[16px]">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" /> Founder‑led consulting
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              Senior attention on architecture decisions and execution alignment.
            </CardContent>
          </Card>
        </div>
      </AnimatedSection>

      {/* Case Studies (optional) */}
      <AnimatedSection as="section" delay={0} className="mx-auto max-w-6xl px-4 py-12">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <SectionTitle
            eyebrow="Proof"
            title="Featured case studies"
            desc="Problem → Solution → Impact. The way decision makers actually evaluate vendors."
          />
          <Button asChild variant="outline" className="bg-background/60">
            <Link to="/insights">Read Insights</Link>
          </Button>
        </div>

        <div className="mt-8 grid gap-4 lg:grid-cols-3">
          {caseStudies.map((c) => (
            <Card key={c.slug} className="border-border/70 bg-background/60 backdrop-blur-[16px]">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-3">
                  <Badge className="bg-primary/10 text-primary hover:bg-primary/10">{c.industry}</Badge>
                  <Badge variant="outline" className="bg-background/60">Case study</Badge>
                </div>
                <CardTitle className="mt-3 text-lg">{c.title}</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                <div className="text-muted-foreground">
                  <span className="font-medium text-foreground">Problem:</span> {c.problem}
                </div>
                <Separator className="my-3" />
                <div className="text-muted-foreground">
                  <span className="font-medium text-foreground">Impact:</span> {c.impact}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </AnimatedSection>

      {/* Careers teaser */}
      <AnimatedSection as="section" delay={0} className="mx-auto max-w-6xl px-4 py-12">
        <div className="rounded-3xl border border-border/70 bg-[radial-gradient(circle_at_25%_20%,rgba(255,145,56,0.18),transparent_55%)] p-1">
          <div className="rounded-[22px] bg-background/70 backdrop-blur-[18px] p-8 lg:p-10">
            <div className="grid gap-8 lg:grid-cols-12 lg:items-center">
              <div className="lg:col-span-7">
                <div className="text-xs font-semibold tracking-widest text-primary/90">
                  Careers
                </div>
                <h3 className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight">
                  Build. Learn. Grow.
                </h3>
                <p className="mt-3 text-sm sm:text-base text-muted-foreground">
                  Join a team built around clarity, ownership, and a 30-hour work week designed for people who build beyond one role.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <Button asChild size="lg">
                    <Link to="/careers">
                      Explore Careers <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="bg-background/60">
                    <Link to="/careers">30-Hour Model</Link>
                  </Button>
                </div>
              </div>
              <div className="lg:col-span-5">
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="item-1" className="border-border/60">
                    <AccordionTrigger className="text-left">What you’ll work on</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      Real client-facing projects, product platform work, and internal tools — with mentorship and weekly feedback.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="item-2" className="border-border/60">
                    <AccordionTrigger className="text-left">How evaluation works</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      Weekly evaluations with clear rubrics, outcomes and ownership. Strong performers get offers.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="item-3" className="border-border/60">
                    <AccordionTrigger className="text-left">What good looks like</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      Quality, communication, and delivery discipline — not just code volume.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Footer CTA */}
      <section className="mx-auto max-w-6xl px-4 pb-16">
        <div className="rounded-3xl border border-border/70 bg-background/60 backdrop-blur-[18px] p-8 lg:p-10 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-20 -right-28 h-[360px] w-[360px] rounded-full bg-[radial-gradient(circle_at_30%_30%,rgba(255,145,56,0.22),transparent_60%)] blur-3xl" />
          <div className="relative grid gap-6 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-7">
              <h3 className="text-2xl sm:text-3xl font-semibold tracking-tight">
                Let’s Build Something Intelligent
              </h3>
              <p className="mt-3 text-sm sm:text-base text-muted-foreground">
                Tell us what you’re building — we’ll come back with a clear plan and a practical next step.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <Button asChild size="lg">
                  <Link to="/contact">Contact</Link>
                </Button>
                <form onSubmit={onNewsletter} className="flex gap-2">
                  <Input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Work email"
                    className="bg-background/70"
                    aria-label="Newsletter email"
                  />
                  <Button type="submit" variant="outline" className="bg-background/70">
                    Subscribe
                  </Button>
                </form>
              </div>
              <div className="mt-2 text-xs text-muted-foreground text-right">
                Occasional insights. No spam.
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
